import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "manualAcceptanceToggle",
    "hidePricesToggle",
    "questionsToggleContainer",
    "questionsContainer",
    "questionsList",
    "questionTemplate",
    "optionTemplate",
    "questionsToggle",
    "hiddenConfig",
    "addButton",
    "maxQuestionsHint"
  ]

  static values = {
    maxQuestions: { type: Number, default: 3 },
    maxOptions: { type: Number, default: 4 },
    questionLabelTemplate: String,
    optionPlaceholderTemplate: String
  }

  connect() {
    this.toggleManualAcceptance()
    this.refreshQuestionCards()
    this.serialize()
  }

  toggleManualAcceptance() {
    if (!this.hasManualAcceptanceToggleTarget) return

    if (this.hasHidePricesToggleTarget) {
      this.hidePricesToggleTarget.disabled = !this.manualAcceptanceToggleTarget.checked
      if (this.hidePricesToggleTarget.disabled && this.hidePricesToggleTarget.checked) {
        this.hidePricesToggleTarget.checked = false
        this.hidePricesToggleTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    // If manual_acceptance is turned off, also turn off questions
    if (!this.manualAcceptanceToggleTarget.checked && this.hasQuestionsToggleTarget && this.questionsToggleTarget.checked) {
      this.questionsToggleTarget.checked = false
      this.questionsToggleTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.toggleQuestions()
  }

  toggleQuestions() {
    if (!this.hasQuestionsContainerTarget || !this.hasQuestionsToggleTarget) return

    // If questions are enabled but manual_acceptance is off, auto-enable it
    if (this.questionsToggleTarget.checked && this.hasManualAcceptanceToggleTarget && !this.manualAcceptanceToggleTarget.checked) {
      this.manualAcceptanceToggleTarget.checked = true
      this.manualAcceptanceToggleTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    const showQuestions = this.questionsToggleTarget.checked
    this.questionsContainerTarget.style.display = showQuestions ? "block" : "none"

    if (showQuestions) {
      this.ensureFirstQuestionCard()
    }

    this.serialize()
  }

  addQuestion(event) {
    event.preventDefault()
    if (this.questionCards.length >= this.maxQuestionsValue) return

    const questionMarkup = this.questionTemplateTarget.innerHTML
      .replaceAll("__QUESTION_ID__", this.generateQuestionId())
      .replaceAll("__QUESTION_INDEX__", String(this.questionCards.length + 1))

    this.questionsListTarget.insertAdjacentHTML("beforeend", questionMarkup)
    this.refreshQuestionCards()
    this.serialize()
  }

  removeQuestion(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-questionnaire-settings-question-card]")?.remove()
    this.refreshQuestionCards()
    this.serialize()
  }

  changeType(event) {
    const card = event.currentTarget.closest("[data-questionnaire-settings-question-card]")
    if (!card) return

    this.ensureMinimumOptions(card)
    this.refreshQuestionCards()
    this.serialize()
  }

  addOption(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-questionnaire-settings-question-card]")
    if (!card) return

    const optionsList = card.querySelector("[data-questionnaire-settings-role='optionsList']")
    const optionCount = this.optionRows(card).length
    if (!optionsList || optionCount >= this.maxOptionsValue) return

    optionsList.appendChild(this.buildOptionRow(optionCount + 1))
    this.refreshQuestionCard(card, this.questionCards.indexOf(card))
    this.serialize()
  }

  removeOption(event) {
    event.preventDefault()
    const card = event.currentTarget.closest("[data-questionnaire-settings-question-card]")
    if (!card || this.optionRows(card).length <= 2) return

    event.currentTarget.closest("[data-questionnaire-settings-option-row]")?.remove()
    this.refreshQuestionCard(card, this.questionCards.indexOf(card))
    this.serialize()
  }

  serialize() {
    if (!this.hasHiddenConfigTarget) return

    const payload = this.questionCards.map((card, index) => {
      const questionType = this.fieldValue(card, "questionType")

      return {
        id: this.fieldValue(card, "questionId"),
        position: index + 1,
        title: this.fieldValue(card, "title").trim(),
        question_type: questionType,
        required: !this.optionalField(card)?.checked,
        options: questionType === "multiple_choice" ? this.fieldValues(card, "option").map((value) => value.trim()) : []
      }
    })

    this.hiddenConfigTarget.value = JSON.stringify(payload)
  }

  manualAcceptanceEnabled() {
    return this.hasManualAcceptanceToggleTarget && this.manualAcceptanceToggleTarget.checked
  }

  refreshQuestionCards() {
    this.questionCards.forEach((card, index) => this.refreshQuestionCard(card, index))
    this.updateAddButton()
    this.updateEmailTypeAvailability()
  }

  refreshQuestionCard(card, index) {
    const label = card.querySelector("[data-questionnaire-settings-role='questionLabel']")
    if (label) {
      label.textContent = this.questionLabel(index + 1)
    }

    const number = card.querySelector("[data-questionnaire-settings-role='questionNumber']")
    if (number) {
      number.textContent = index + 1
    }

    this.ensureMinimumOptions(card)
    this.updateOptionsVisibility(card)
    this.updateOptionControls(card)
  }

  updateOptionsVisibility(card) {
    const optionsContainer = card.querySelector("[data-questionnaire-settings-role='optionsContainer']")
    if (!optionsContainer) return

    optionsContainer.style.display = this.fieldValue(card, "questionType") === "multiple_choice" ? "block" : "none"
  }

  updateOptionControls(card) {
    const optionRows = this.optionRows(card)
    optionRows.forEach((row, index) => {
      const input = row.querySelector("[data-questionnaire-settings-field='option']")
      if (input) {
        input.placeholder = this.optionPlaceholder(index + 1)
      }

      const button = row.querySelector("[data-action='questionnaire-settings#removeOption']")
      if (button) {
        button.disabled = optionRows.length <= 2
      }
    })

    const addButton = card.querySelector("[data-action='questionnaire-settings#addOption']")
    if (addButton) {
      addButton.disabled = optionRows.length >= this.maxOptionsValue
    }
  }

  updateAddButton() {
    if (!this.hasAddButtonTarget) return

    const count = this.questionCards.length
    const maxReached = count >= this.maxQuestionsValue
    this.addButtonTarget.style.display = maxReached ? "none" : ""
    this.addButtonTarget.classList.toggle("questionnaire-add-row--no-questions", count === 0)

    if (this.hasMaxQuestionsHintTarget) {
      this.maxQuestionsHintTarget.style.display = maxReached ? "" : "none"
    }
  }

  updateEmailTypeAvailability() {
    const emailSelects = this.questionCards.filter((card) => this.fieldValue(card, "questionType") === "email_address")

    this.questionCards.forEach((card) => {
      const select = card.querySelector("[data-questionnaire-settings-field='questionType']")
      if (!select) return

      const emailOption = Array.from(select.options).find((option) => option.value === "email_address")
      if (!emailOption) return

      emailOption.disabled = emailSelects.length > 0 && select.value !== "email_address"
    })
  }

  ensureMinimumOptions(card) {
    if (this.fieldValue(card, "questionType") !== "multiple_choice") return

    const optionsList = card.querySelector("[data-questionnaire-settings-role='optionsList']")
    if (!optionsList) return

    let optionCount = this.optionRows(card).length
    while (optionCount < 2) {
      optionsList.appendChild(this.buildOptionRow(optionCount + 1))
      optionCount++
    }
  }

  buildOptionRow(number) {
    const markup = this.optionTemplateTarget.innerHTML
      .replace("__OPTION_PLACEHOLDER__", this.optionPlaceholder(number))
    const container = document.createElement("div")
    container.innerHTML = markup
    return container.firstElementChild
  }

  ensureFirstQuestionCard() {
    if (this.questionCards.length > 0) return
    if (this.storedQuestionCount() > 0) return

    const questionMarkup = this.questionTemplateTarget.innerHTML
      .replaceAll("__QUESTION_ID__", this.generateQuestionId())
      .replaceAll("__QUESTION_INDEX__", "1")

    this.questionsListTarget.insertAdjacentHTML("beforeend", questionMarkup)
    this.refreshQuestionCards()
  }

  storedQuestionCount() {
    if (!this.hasHiddenConfigTarget) return 0

    try {
      const parsedConfig = JSON.parse(this.hiddenConfigTarget.value || "[]")
      return Array.isArray(parsedConfig) ? parsedConfig.length : 0
    } catch {
      return 0
    }
  }

  optionRows(card) {
    return Array.from(card.querySelectorAll("[data-questionnaire-settings-option-row]"))
  }

  fieldValue(card, fieldName) {
    const field = card.querySelector(`[data-questionnaire-settings-field='${fieldName}']`)
    return field ? field.value : ""
  }

  fieldValues(card, fieldName) {
    return Array.from(card.querySelectorAll(`[data-questionnaire-settings-field='${fieldName}']`)).map((field) => field.value)
  }

  optionalField(card) {
    return card.querySelector("[data-questionnaire-settings-field='optional']")
  }

  optionPlaceholder(number) {
    const template = this.hasOptionPlaceholderTemplateValue ? this.optionPlaceholderTemplateValue : "Option __NUMBER__"
    return template.replace("__NUMBER__", String(number))
  }

  questionLabel(number) {
    const template = this.hasQuestionLabelTemplateValue ? this.questionLabelTemplateValue : "Question __NUMBER__"
    return template.replace("__NUMBER__", String(number))
  }

  generateQuestionId() {
    if (window.crypto?.randomUUID) {
      return `q_${window.crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`
    }

    return `q_${Date.now()}${Math.floor(Math.random() * 1000)}`
  }

  get questionCards() {
    return Array.from(this.questionsListTarget.querySelectorAll("[data-questionnaire-settings-question-card]"))
  }
}
